// Mirror of the backend's soft ranking — celer-backend/util/auto-assign-score.js.
// Keep the two in step; the simulator is only worth anything if it agrees with
// the server it is describing.
//
// Every term scores 0-100 and the final score is their weighted average, also
// 0-100. A term that cannot be answered returns null and drops out of the
// average rather than scoring 0 — a rider whose position is unknown is ranked on
// everything else, not punished for a sleeping phone.

export const DEFAULT_SCORING = {
	// Shares of one whole decision — the four add up to 1. See weightBudget.js;
	// the engine itself is scale-invariant, so older 0-100 values still work.
	weightIdle: 1,
	weightDistance: 0,
	weightLoad: 0,
	weightBatching: 0,

	idleFullMinutes: 30,
	distanceFullMeters: 3000,
	loadFullOrders: 3,
	// Above this, one delivery counts as more than one order's worth of load.
	// 0 = off, every order counts as exactly one.
	loadTripReferenceMeters: 0,
	// How much the number of free riders right now shifts the balance between
	// spreading work out and stacking it. 0 = off, 100 = full swing.
	supplySensitivity: 0,

	batchPickupFullMeters: 800,
	batchDropoffFullMeters: 800,
	batchReadyToleranceMinutes: 8,
	// How far ahead the sweep may look for a not-yet-ready order, purely to add it
	// to a batch. 0 = off. The one setting that lets automatic assignment touch an
	// order before it is ready, and only ever as an addition to a batch whose
	// timing was checked.
	batchLookaheadMinutes: 0,
	// How far BEFORE ready the check may take an order on its own merit. 0 = only
	// ready orders. Switched off by readyGraceSeconds or the pending-at-least
	// limit, both of which exist to delay assignment relative to ready.
	preReadyMinutes: 0,
	// 1 = the old behaviour: one order per rider per pass. Raising it is what
	// turns in-pass batching on, and it should be raised together with a batching
	// weight — with that at zero nothing rewards a good batch.
	// 0 = no ceiling on how many orders a rider holds — the behaviour every city
	// had before this setting existed.
	maxBatchSize: 0,

	maxPositionAgeMinutes: 10,
	maxDistanceMeters: 0,

	// A rider scoring below this is not given the order at all. 0 = off, 0-100 on
	// the score's own scale — a floor, never a way to steer the ranking.
	minAssignScore: 0,

	// Hands-off window after an order becomes ready, in seconds. Applies to the
	// sweep and to the on-ready trigger, never to on-creation, which exists to
	// assign before an order is ready.
	readyGraceSeconds: 0,

	// How much waiting outweighs being a good match. Urgency is 0-100 like the
	// score, so 100 means waiting spans 0-10.000 against a 0-100 match: strict
	// oldest-first, and match quality only breaks ties. The shipped modes lower
	// it to 5-20 to let the pairing lead.
	orderUrgencyWeight: 100,
	urgencyFullMinutes: 15,

	// Whether an assigned order is pushed to its rider again while they have not
	// acknowledged it, and how many times. Off means one push and nothing more.
	// The confirm window is derived from these — see confirmWindowSeconds; 15
	// holds an unanswered order for 240 seconds.
	notifyResendEnabled: true,
	notifyResendCount: 15,

	// Whether an unacknowledged automatic assignment is taken back at all, and
	// after how long. On is what every city has run: the order goes back to the
	// list and the next pass gives it to somebody else. Off means the assignment
	// stands until a person moves it — no bouncing, but a rider who has gone home
	// keeps the order until somebody notices.
	//
	// The seconds are a floor: 0 = follow the notification schedule
	// (confirmWindowSeconds), and an explicit value can only lengthen the wait.
	revokeUnansweredEnabled: true,
	revokeUnansweredSeconds: 0,

	// When an admin takes an automatically assigned order off a rider, may the
	// check hand that same order back to that same rider? Off = it may, which is
	// what every city has done until now. On puts the rider on the same per-order
	// ignore list a timed-out assignment uses. Automatic assignments only — an
	// order the rider chose themselves says nothing about the check.
	blockAdminRemovedRider: false,

	// Once a rider reaches the orders-in-hand limit, must they empty their hands
	// before the check gives them anything again? Off, the limit is read fresh on
	// every pass, so a rider who drops from three to two is handed a third within
	// seconds and never leaves the ceiling. On, reaching it locks them out until
	// they hold nothing — however they got there. Does nothing at a limit of 1.
	holdRiderUntilEmpty: false,
};

// celer-backend/util/auto-assign-score.js — RESEND_INTERVAL_SECONDS. How far
// apart the repeats go; not configurable, because the spacing is about how long
// a mounted phone takes to be noticed rather than about how a city works.
export const RESEND_INTERVAL_SECONDS = 15;

// Never below this, however few pushes are configured: every city has run 45
// seconds since automatic assignment existed, and switching resends off is a
// statement about nagging riders, not about snatching orders back sooner.
const CONFIRM_WINDOW_FLOOR_SECONDS = 45;

// How long an unanswered automatic assignment is actually held, or null when
// the city has switched the takeback off. null is an answer, not a missing one
// — callers must not fall back to a number when they get it.
export const resolveRevokeWindowSeconds = scoring => {
	if (scoring?.revokeUnansweredEnabled === false) return null;

	const derived = confirmWindowSeconds(scoring);
	const explicit = Number(scoring?.revokeUnansweredSeconds);

	// 0, and anything unparseable, means "follow the notification schedule" —
	// what every city ran before this was a setting.
	if (!Number.isFinite(explicit) || explicit <= 0) return derived;

	return Math.max(derived, Math.round(explicit));
};

// celer-backend/util/auto-assign-score.js. Past this it is not waiting for an
// answer any more; a city that wants longer wants the takeback off.
export const MAX_REVOKE_WINDOW_SECONDS = 1800;

// The ceiling on repeats — celer-backend/util/auto-assign-score.js. Chosen from
// the confirm window rather than the count: 20 repeats hold an order 315
// seconds, and past roughly five minutes it is no longer waiting for a rider to
// answer, it is lost.
export const MAX_RESEND_COUNT = 20;

// 0 when resends are off, otherwise 1..MAX_RESEND_COUNT.
export const resolveResendCount = scoring => {
	if (scoring?.notifyResendEnabled === false) return 0;

	const n = Number(scoring?.notifyResendCount);
	if (!Number.isFinite(n)) return DEFAULT_SCORING.notifyResendCount;

	return Math.max(1, Math.min(MAX_RESEND_COUNT, Math.round(n)));
};

// How long an automatically assigned order waits for its rider before it is
// taken back: one resend interval after the last push. Holding it open longer
// cannot produce an acknowledgement — nothing further is being sent.
export const confirmWindowSeconds = scoring =>
	Math.max(
		CONFIRM_WINDOW_FLOOR_SECONDS,
		RESEND_INTERVAL_SECONDS * (resolveResendCount(scoring) + 1),
	);

// How much an order needs a rider, 0-100. Time spent READY, not age: an order
// placed an hour ago with a fifty-minute preparation has waited ten minutes.
export const orderUrgency = ({ readyAt, now }, scoring) => {
	if (readyAt == null || now <= readyAt) return 0;
	const full = Number(scoring.urgencyFullMinutes) || 1;
	return clamp100(((now - readyAt) / 60000 / full) * 100);
};

export const SCORE_TERMS = [
	{ key: 'idle', weightKey: 'weightIdle', label: 'Χρόνος χωρίς παραγγελία' },
	{ key: 'distance', weightKey: 'weightDistance', label: 'Απόσταση από το κατάστημα' },
	{ key: 'load', weightKey: 'weightLoad', label: 'Φόρτος' },
	{ key: 'batching', weightKey: 'weightBatching', label: 'Συνδυασμός παραγγελιών' },
];

export const resolveScoring = scoring => ({ ...DEFAULT_SCORING, ...(scoring || {}) });

const clamp100 = n => Math.max(0, Math.min(100, n));

// A value that is not a number is not an answer — null, so the term drops out
// instead of leaking NaN into the weighted average.
const ramp = (value, best, worst) => {
	if (!(worst > best)) return null;
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return clamp100(((worst - n) / (worst - best)) * 100);
};

// celer-backend/util/radius.js — getRadiusMeters, same arithmetic.
const EARTH_RADIUS_METERS = 6371000;
const toRadians = deg => (deg * Math.PI) / 180;

// Same coercion rules as the backend's toNumber: JSONB rows saved from forms
// can carry coordinates as strings, and the two implementations must not
// disagree about whether such a point exists.
const toNumber = value => {
	const n = typeof value === 'string' ? Number(value) : value;
	return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

export const metersBetween = (from, to) => {
	const lat1 = toNumber(from?.lat);
	const lng1 = toNumber(from?.lng);
	const lat2 = toNumber(to?.lat);
	const lng2 = toNumber(to?.lng);

	if (lat1 === null || lng1 === null || lat2 === null || lng2 === null) {
		return null;
	}

	// 0,0 is what a missing coordinate collapses to, and the server rejects it —
	// so this has to as well, or the two disagree about who is where.
	if ((lat1 === 0 && lng1 === 0) || (lat2 === 0 && lng2 === 0)) {
		return null;
	}

	const dLat = toRadians(lat2 - lat1);
	const dLng = toRadians(lng2 - lng1);

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

	return Math.round(EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

// Mirror of computeRouteCost in celer-backend/util/auto-assign-score.js.
//
// What adding this pickup costs THIS rider, in metres. A free rider is simply
// their distance to the shop. A rider already committed somewhere pays whichever
// is cheaper: picking up on the way (the extra metres added to a leg they are
// already travelling) or finishing first and then going. The scale therefore
// means the same thing for everybody — extra metres travelled.
const isPlaceable = point => metersBetween(point, point) !== null;

export const computeRouteCost = ({ from, waypoints = [], to }) => {
	const empty = {
		meters: null,
		rawMeters: null,
		mode: null,
		detourMeters: null,
		afterMeters: null,
	};

	if (!isPlaceable(to)) return empty;

	const stops = (waypoints || []).filter(isPlaceable);
	const rawMeters = metersBetween(from, to);

	if (!stops.length) {
		return { ...empty, meters: rawMeters, rawMeters, mode: rawMeters === null ? null : 'direct' };
	}

	if (rawMeters === null) {
		// No usable fix, but their committed stops are known facts rather than
		// sensor readings — worth using, and better than no opinion at all.
		const meters = Math.max(...stops.map(w => metersBetween(w, to) ?? 0));
		return { ...empty, meters, mode: 'destinationOnly', afterMeters: meters };
	}

	let detourMeters = Infinity;
	let furthest = null;

	for (const w of stops) {
		const toStop = metersBetween(from, w);
		const stopToShop = metersBetween(w, to);
		if (toStop === null || stopToShop === null) continue;

		detourMeters = Math.min(detourMeters, rawMeters + stopToShop - toStop);
		if (!furthest || toStop > furthest.toStop) furthest = { toStop, stopToShop };
	}

	if (!furthest) return { ...empty, meters: rawMeters, rawMeters, mode: 'direct' };

	detourMeters = Math.max(0, Math.round(detourMeters));
	const afterMeters = furthest.toStop + furthest.stopToShop;
	const meters = Math.min(detourMeters, afterMeters);

	return {
		meters,
		rawMeters,
		mode: meters === detourMeters ? 'enroute' : 'afterDrop',
		detourMeters,
		afterMeters,
	};
};

// How short of riders this moment is: 0 when free riders outnumber the orders
// waiting, 1 when nobody is free at all.
export const computeSupplyScarcity = ({ freeRiders, waitingOrders }) => {
	const demand = Math.max(1, Number(waitingOrders) || 0);
	const supply = Math.max(0, Number(freeRiders) || 0);
	return Math.max(0, Math.min(1, 1 - supply / demand));
};

// What scarcity does to the ranking: riders to spare means load counts for more
// and batching for less (spread the work); nobody free means the reverse
// (stacking is the only way the board clears, so reward stacking well).
export const supplyWeightFactors = (scarcity, scoring) => {
	const k = Math.max(0, Math.min(1, (Number(scoring?.supplySensitivity) || 0) / 100));
	if (!k || scarcity === null || scarcity === undefined) return { load: 1, batching: 1 };

	const swing = k * (1 - 2 * Math.max(0, Math.min(1, scarcity)));
	return { load: 1 + swing, batching: 1 - swing };
};

// The two hard limits, mirroring what rankForOrder does in
// celer-backend/util/auto-assign-order.js. They are not part of scoreCandidate
// because they are not scores: one silences a criterion, the other removes the
// rider from the running altogether.
//
// Both the simulator and the worked example go through here, so they cannot
// disagree about who is out.
export const resolveDistanceForScoring = ({ positionAgeMinutes, meters, scoring }) => {
	const maxAge = Number(scoring.maxPositionAgeMinutes);

	// How much the fix is worth believing, 1 down to 0 — not a yes/no. See
	// positionTrust in celer-backend/util/auto-assign-score.js for why.
	let trust;
	if (
		positionAgeMinutes === null ||
		positionAgeMinutes === undefined ||
		positionAgeMinutes === ''
	) {
		trust = 0;
	} else if (!(maxAge > 0)) {
		trust = 1;
	} else {
		trust = Math.max(0, Math.min(1, 1 - Number(positionAgeMinutes) / maxAge));
	}

	// A missing or fully-aged fix does not disqualify anyone — it only means the
	// distance criterion has nothing to say about them.
	const usableMeters = trust > 0 ? (meters ?? null) : null;

	const ceiling = Number(scoring.maxDistanceMeters);
	const tooFar = ceiling > 0 && usableMeters !== null && usableMeters > ceiling;

	return { trust, meters: usableMeters, tooFar, ceiling };
};

const scoreIdle = ({ idleMinutes }, scoring) => {
	if (idleMinutes === null) return 100;
	// Missing fact ≠ never completed: no answer, so the term drops out.
	const n = Number(idleMinutes);
	if (!Number.isFinite(n)) return null;
	return clamp100((n / Number(scoring.idleFullMinutes || 1)) * 100);
};

const scoreDistance = ({ meters }, scoring) =>
	meters === null || meters === undefined
		? null
		: ramp(meters, 0, Number(scoring.distanceFullMeters));

const scoreLoad = ({ liveOrders, effectiveLoad }, scoring) =>
	ramp(effectiveLoad ?? liveOrders, 0, Number(scoring.loadFullOrders));

// What a rider's held orders are worth as load once delivery length is taken
// into account. Orders with no coordinates count as one — unknown is not short.
export const computeEffectiveLoad = ({ held = [], scoring }) => {
	const reference = Number(scoring.loadTripReferenceMeters) || 0;
	if (!(reference > 0)) return held.length;

	return held.reduce((sum, h) => {
		const trip = metersBetween(h.shop, h.dest);
		if (trip === null) return sum + 1;
		return sum + Math.max(1, trip / reference);
	}, 0);
};

// Mirror of computeBatchFit in celer-backend/util/auto-assign-score.js. Three
// questions, combined with `min` because a batch is only as good as its worst
// answer. What applies depends on whether the rider has already collected the
// order being compared against — a collected one contributes destination
// proximity only, because that trip to the counter has already happened and its
// ready time is history.
export const computeBatchFit = ({ held = [], newOrder, scoring }) => {
	// Nothing to batch with is NO batch, not a bad one: the criterion drops out
	// rather than scoring 0, so an empty-handed rider does not look identical to
	// one holding an order this would combine terribly with.
	if (!held.length) return { fit: null, matched: null, parts: [] };

	let best = null;

	for (const h of held) {
		const parts = [];

		if (!h.carrying) {
			const meters = metersBetween(h.shop, newOrder.shop);
			const score = ramp(meters, 0, Number(scoring.batchPickupFullMeters));
			if (meters !== null && score !== null) parts.push({ key: 'pickup', score, meters });
		}

		const dropMeters = metersBetween(h.dest, newOrder.dest);
		const dropScore = ramp(dropMeters, 0, Number(scoring.batchDropoffFullMeters));
		if (dropMeters !== null && dropScore !== null) {
			parts.push({ key: 'dropoff', score: dropScore, meters: dropMeters });
		}

		if (!h.carrying && h.readyAt != null && newOrder.readyAt != null) {
			const minutes = Math.abs(newOrder.readyAt - h.readyAt) / 60000;
			const score = ramp(minutes, 0, Number(scoring.batchReadyToleranceMinutes));
			if (score !== null) parts.push({ key: 'ready', score, minutes });
		}

		if (!parts.length) continue;

		const fit = Math.min(...parts.map(p => p.score));
		if (!best || fit > best.fit) best = { fit, matched: h, parts };
	}

	// Holding orders we cannot judge is not the same as holding none.
	return best || { fit: null, matched: null, parts: [] };
};

// null OR undefined both mean "nothing to judge" — the term drops out.
const scoreBatching = ({ batchFit }) => batchFit ?? null;

export const scoreCandidate = ({ facts, scoring }) => {
	// An older fix does not make a rider look further away — it makes the distance
	// criterion count for less, and the other criteria take up the slack.
	const trust = facts.positionTrust ?? (facts.meters === null ? 0 : 1);

	// How busy the city is right now, applied the same way trust is: a multiplier
	// on a configured weight rather than a fifth column nobody asked for.
	const supply = supplyWeightFactors(facts.supplyScarcity, scoring);

	const terms = [
		{ key: 'idle', weight: Number(scoring.weightIdle), score: scoreIdle(facts, scoring) },
		{
			key: 'distance',
			weight: Number(scoring.weightDistance) * trust,
			score: scoreDistance(facts, scoring),
		},
		{
			key: 'load',
			weight: Number(scoring.weightLoad) * supply.load,
			score: scoreLoad(facts, scoring),
		},
		{
			key: 'batching',
			weight: Number(scoring.weightBatching) * supply.batching,
			score: scoreBatching(facts, scoring),
		},
	];

	const used = terms.filter(t => t.score !== null && isFinite(t.weight) && t.weight > 0);
	const totalWeight = used.reduce((sum, t) => sum + t.weight, 0);

	const score = totalWeight
		? used.reduce((sum, t) => sum + t.weight * t.score, 0) / totalWeight
		: scoreIdle(facts, scoring);

	return {
		score: Math.round(score),
		terms: terms.map(t => ({
			...t,
			score: t.score === null ? null : Math.round(t.score),
			used: used.includes(t),
		})),
		facts,
	};
};
